import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  BarChart3,
  Loader2,
  Calendar,
  Users,
  Clock,
  Building2,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Download,
  PieChart,
  Activity,
  User
} from "lucide-react";

export default function ReportesVisitasPage() {
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [dateRange, setDateRange] = useState({
    desde: new Date(new Date().setDate(1)).toISOString().split('T')[0], // First day of month
    hasta: new Date().toISOString().split('T')[0] // Today
  });
  
  const { getAuthHeaders } = useAuth();

  const fetchReportData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/visitantes/reportes?fecha_desde=${dateRange.desde}&fecha_hasta=${dateRange.hasta}`,
        getAuthHeaders()
      );
      setReportData(response.data);
    } catch (err) {
      toast.error("Error al cargar datos del reporte");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, dateRange]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const handleExport = async (formato) => {
    try {
      const response = await axios.get(
        `${API}/visitantes/reportes/exportar?fecha_desde=${dateRange.desde}&fecha_hasta=${dateRange.hasta}&formato=${formato}`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reporte_visitas_${dateRange.desde}_${dateRange.hasta}.${formato}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Reporte exportado en formato ${formato.toUpperCase()}`);
    } catch (err) {
      toast.error("Error al exportar reporte");
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
        <Sidebar />
        <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="reportes-visitas-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
                Reportes de Visitas
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Estadísticas y análisis de visitantes
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => handleExport('xlsx')}>
              <Download className="w-4 h-4 mr-2" />
              Excel
            </Button>
            <Button variant="outline" onClick={() => handleExport('pdf')}>
              <Download className="w-4 h-4 mr-2" />
              PDF
            </Button>
          </div>
        </div>

        {/* Date Range Filter */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-slate-400" />
                <span className="font-medium text-slate-700">Período:</span>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={dateRange.desde}
                  onChange={(e) => setDateRange({ ...dateRange, desde: e.target.value })}
                  className="w-40"
                />
                <ArrowRight className="w-4 h-4 text-slate-400" />
                <Input
                  type="date"
                  value={dateRange.hasta}
                  onChange={(e) => setDateRange({ ...dateRange, hasta: e.target.value })}
                  className="w-40"
                />
              </div>
              <Button onClick={fetchReportData} className="bg-blue-600 hover:bg-blue-700">
                Actualizar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Visitas</p>
                  <p className="text-3xl font-bold text-blue-600">{reportData?.totalVisitas || 0}</p>
                </div>
                <Users className="w-10 h-10 text-blue-400" />
              </div>
              {reportData?.comparacionPeriodoAnterior && (
                <div className="mt-2 flex items-center gap-1 text-sm">
                  {reportData.comparacionPeriodoAnterior > 0 ? (
                    <>
                      <TrendingUp className="w-4 h-4 text-green-500" />
                      <span className="text-green-600">+{reportData.comparacionPeriodoAnterior}%</span>
                    </>
                  ) : (
                    <>
                      <TrendingDown className="w-4 h-4 text-red-500" />
                      <span className="text-red-600">{reportData.comparacionPeriodoAnterior}%</span>
                    </>
                  )}
                  <span className="text-slate-500">vs período anterior</span>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Visitantes Únicos</p>
                  <p className="text-3xl font-bold text-green-600">{reportData?.visitantesUnicos || 0}</p>
                </div>
                <Activity className="w-10 h-10 text-green-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-amber-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Tiempo Promedio</p>
                  <p className="text-3xl font-bold text-amber-600">{reportData?.tiempoPromedio || "0h 0m"}</p>
                </div>
                <Clock className="w-10 h-10 text-amber-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-purple-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Promedio Diario</p>
                  <p className="text-3xl font-bold text-purple-600">{reportData?.promedioDiario || 0}</p>
                </div>
                <BarChart3 className="w-10 h-10 text-purple-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Visitas por Departamento */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Visitas por Departamento
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reportData?.visitasPorDepartamento?.length > 0 ? (
                <div className="space-y-3">
                  {reportData.visitasPorDepartamento.map((dept, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="text-slate-600">{dept.nombre}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-500 rounded-full"
                            style={{ width: `${(dept.cantidad / reportData.totalVisitas) * 100}%` }}
                          />
                        </div>
                        <Badge variant="outline">{dept.cantidad}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">No hay datos disponibles</p>
              )}
            </CardContent>
          </Card>

          {/* Visitas por Tipo */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChart className="w-5 h-5" />
                Visitas por Tipo
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reportData?.visitasPorTipo?.length > 0 ? (
                <div className="space-y-3">
                  {reportData.visitasPorTipo.map((tipo, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: tipo.color || '#6366f1' }}
                        />
                        <span className="text-slate-600">{tipo.nombre}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full"
                            style={{ 
                              width: `${(tipo.cantidad / reportData.totalVisitas) * 100}%`,
                              backgroundColor: tipo.color || '#6366f1'
                            }}
                          />
                        </div>
                        <Badge variant="outline">{tipo.cantidad}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">No hay datos disponibles</p>
              )}
            </CardContent>
          </Card>

          {/* Visitas por Día de la Semana */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Distribución Semanal
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reportData?.visitasPorDiaSemana?.length > 0 ? (
                <div className="flex items-end justify-between h-40 gap-2">
                  {reportData.visitasPorDiaSemana.map((dia, idx) => {
                    const maxVal = Math.max(...reportData.visitasPorDiaSemana.map(d => d.cantidad));
                    const height = maxVal > 0 ? (dia.cantidad / maxVal) * 100 : 0;
                    return (
                      <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                        <span className="text-xs font-medium text-slate-600">{dia.cantidad}</span>
                        <div 
                          className="w-full bg-gradient-to-t from-indigo-500 to-indigo-400 rounded-t-lg transition-all"
                          style={{ height: `${height}%`, minHeight: '4px' }}
                        />
                        <span className="text-xs text-slate-500">{dia.dia}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">No hay datos disponibles</p>
              )}
            </CardContent>
          </Card>

          {/* Horas Pico */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Horas Pico
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reportData?.horasPico?.length > 0 ? (
                <div className="space-y-2">
                  {reportData.horasPico.map((hora, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                      <span className="font-mono text-slate-600">{hora.hora}:00 - {hora.hora}:59</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-amber-500 rounded-full"
                            style={{ width: `${(hora.cantidad / reportData.totalVisitas) * 100}%` }}
                          />
                        </div>
                        <Badge className="bg-amber-100 text-amber-700">{hora.cantidad}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">No hay datos disponibles</p>
              )}
            </CardContent>
          </Card>

          {/* Visitas por Persona Visitada */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Personas Más Visitadas
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reportData?.visitasPorPersona?.length > 0 ? (
                <div className="space-y-3">
                  {reportData.visitasPorPersona.map((persona, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          persona.es_otros 
                            ? 'bg-purple-100 text-purple-600' 
                            : 'bg-green-100 text-green-600'
                        }`}>
                          <User className="w-4 h-4" />
                        </div>
                        <span className="text-slate-600">{persona.nombre}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${
                              persona.es_otros ? 'bg-purple-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${(persona.cantidad / reportData.totalVisitas) * 100}%` }}
                          />
                        </div>
                        <Badge variant="outline" className={
                          persona.es_otros ? 'border-purple-200 text-purple-700' : ''
                        }>
                          {persona.cantidad}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">No hay datos disponibles</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Top Visitantes Frecuentes */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Visitantes Más Frecuentes
            </CardTitle>
            <CardDescription>Top 10 visitantes con más visitas en el período</CardDescription>
          </CardHeader>
          <CardContent>
            {reportData?.visitantesFrecuentes?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left p-3 font-medium text-slate-600">#</th>
                      <th className="text-left p-3 font-medium text-slate-600">Nombre</th>
                      <th className="text-left p-3 font-medium text-slate-600">Cédula</th>
                      <th className="text-left p-3 font-medium text-slate-600">Empresa</th>
                      <th className="text-left p-3 font-medium text-slate-600">Visitas</th>
                      <th className="text-left p-3 font-medium text-slate-600">Última Visita</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.visitantesFrecuentes.map((v, idx) => (
                      <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-3">
                          <Badge className={
                            idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                            idx === 1 ? 'bg-slate-200 text-slate-700' :
                            idx === 2 ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-600'
                          }>
                            {idx + 1}
                          </Badge>
                        </td>
                        <td className="p-3 font-medium">{v.nombre}</td>
                        <td className="p-3 font-mono text-sm">{v.cedula}</td>
                        <td className="p-3">{v.empresa || "-"}</td>
                        <td className="p-3">
                          <Badge className="bg-indigo-100 text-indigo-700">{v.totalVisitas}</Badge>
                        </td>
                        <td className="p-3 text-sm text-slate-500">
                          {new Date(v.ultimaVisita).toLocaleDateString("es-DO")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center text-slate-500 py-8">No hay datos disponibles</p>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
